/**
 * Shared Runner Status Calculation
 *
 * Canonical implementation used across all platforms (backend, mobile, web, public-live).
 * Consolidates logic previously duplicated in:
 * - backend/src/utils/runnerStatus.ts
 * - mobile/src/components/AllRunnersList.tsx
 * - public-live/src/lib/runnerStatus.ts
 * - web/src/components/RDDashboard.tsx
 * - web/src/components/EnhancedRaceDashboard.tsx
 *
 * Status Priority (highest to lowest):
 * 1. DNS (runner.dns === true)
 * 2. DNF (has active dnf event)
 * 3. Finished (has active finish event)
 * 4. Waiting for race start (race='draft', no race_start event)
 * 5. Runner start pending (race='active', no race_start event)
 * 6. Enroute to {Station} (calculate from latest event)
 * 7. Unknown (fallback)
 */

import type {
  RunnerDisplayStatus,
  StatusCode,
  StatusEvent,
  StatusStation,
  RunnerStatusResult,
} from './types';

/**
 * Canonical "event time" for a check-in event: when the runner was actually at
 * the station, per the volunteer's device.
 *
 * WHY this exists (see AD-228): every event carries two timestamps.
 * - `device_timestamp` — set on the volunteer's device at the moment of the scan.
 *   Preserved verbatim through sync. This is the real field time — the truth.
 * - `server_timestamp` — `DEFAULT now()`, i.e. when the row reached the server.
 *   When a device is offline and later dumps its queued scans, every row in that
 *   batch gets a `server_timestamp` stamped at the sync moment, NOT the scan
 *   moment. Reading it then makes runners look like they arrived/left hours late
 *   (and can even put a check-out *before* its check-in when the two events land
 *   in different sync batches).
 *
 * So anything that represents "when the runner was there" — display, chronological
 * sort, elapsed/split/pace, placement — must use `device_timestamp`. We fall back
 * to `server_timestamp` only for the rare legacy/synthetic row that has no device
 * time. (`server_timestamp` is still the right field for pure sync bookkeeping —
 * "when did the server learn of this" — e.g. closeout timing; those callers keep
 * reading it directly.)
 */
export function eventTime(e: {
  device_timestamp?: string | null;
  server_timestamp?: string | null;
}): string {
  return e.device_timestamp || e.server_timestamp || '';
}

/**
 * Get the next station in the race sequence after the given station.
 *
 * Filters out:
 * - Parent loop stations (expected_visits > 1) - grouping only, no events
 * - Inherent stations (check_mode = 'start' or 'finish') - not physical checkpoints
 *
 * @param stations - All stations for the race
 * @param currentStationId - Station the runner was last seen at (null for race start)
 * @returns The next station, or null if at/past the last checkpoint
 */
function getNextStation(
  stations: StatusStation[],
  currentStationId?: string | null
): StatusStation | null {
  const actualCheckpoints = stations.filter(s =>
    s.position != null &&
    (s.expected_visits == null || s.expected_visits <= 1) &&
    s.check_mode !== 'start' &&
    s.check_mode !== 'finish'
  );

  const sorted = [...actualCheckpoints].sort((a, b) => a.position - b.position);

  if (sorted.length === 0) {
    return null;
  }

  if (!currentStationId) {
    return sorted[0] ?? null;
  }

  const currentIndex = sorted.findIndex(s => s.id === currentStationId);

  if (currentIndex === -1) {
    return sorted[0] ?? null;
  }

  if (currentIndex + 1 < sorted.length) {
    return sorted[currentIndex + 1] ?? null;
  }

  return null;
}

/**
 * Map detailed status to compact status code for CSS styling and sorting.
 */
function toStatusCode(status: RunnerDisplayStatus): StatusCode {
  switch (status) {
    case 'dns': return 'dns';
    case 'dnf': return 'dnf';
    case 'finished': return 'finished';
    case 'waiting_for_race_start':
    case 'runner_start_pending':
    case 'at_station':
    case 'enroute':
      return 'on_course';
    case 'unknown':
    default:
      return 'unknown';
  }
}

/**
 * Get hex color for a runner display status.
 */
export function getStatusColor(status: RunnerDisplayStatus): string {
  switch (status) {
    case 'dns':
      return '#f59e0b'; // Amber
    case 'dnf':
      return '#dc2626'; // Red
    case 'finished':
      return '#2563eb'; // Blue
    case 'waiting_for_race_start':
    case 'runner_start_pending':
      return '#6b7280'; // Gray
    case 'at_station':
      return '#16a34a'; // Green — same as check-in button, runner is physically present
    case 'enroute':
      return '#8b5cf6'; // Purple
    case 'unknown':
    default:
      return '#9ca3af'; // Light gray
  }
}

/**
 * Get CSS-friendly status class name.
 */
export function getStatusColorClass(status: RunnerDisplayStatus): string {
  switch (status) {
    case 'dns': return 'status-dns';
    case 'dnf': return 'status-dnf';
    case 'finished': return 'status-finished';
    case 'waiting_for_race_start':
    case 'runner_start_pending':
      return 'status-pending';
    case 'at_station': return 'status-at-station';
    case 'enroute': return 'status-enroute';
    case 'unknown':
    default: return 'status-unknown';
  }
}

/**
 * Get CSS class for compact status code (matches public-live styling).
 */
export function getStatusCssClass(statusCode: StatusCode): string {
  switch (statusCode) {
    case 'dns': return 'dns';
    case 'dnf': return 'dropped';
    case 'finished': return 'finished';
    case 'on_course':
    case 'unknown':
    default: return 'on_course';
  }
}

/**
 * Calculate runner display status based on events and race state.
 *
 * @param runner - Runner object with dns flag
 * @param events - Array of runner's events (any sort order - sorted internally)
 * @param raceStatus - Current race status ('draft', 'active', 'completed')
 * @param stations - Array of aid stations for the race
 * @returns Complete status result with label, code, color, and display flags
 */
export function calculateRunnerStatus(
  runner: { dns: boolean },
  events: StatusEvent[],
  raceStatus: 'draft' | 'active' | 'completed',
  stations: StatusStation[] = []
): RunnerStatusResult {
  // Filter out cancelled events before processing
  const activeEvents = events.filter(e => !e.cancelled_at);

  // Priority 1: DNS flag takes precedence over everything
  if (runner.dns) {
    const status: RunnerDisplayStatus = 'dns';
    return {
      status,
      statusLabel: 'DNS',
      statusCode: toStatusCode(status),
      isDropped: false,
      color: getStatusColor(status),
    };
  }

  // Sort by timestamp, most recent first. Use the real scan time
  // (device_timestamp) so an offline-synced batch doesn't reorder events and pick
  // the wrong "latest" event — which would misreport status/next-station. See
  // eventTime() above (AD-228).
  const sortedEvents = [...activeEvents].sort((a, b) => {
    const timeA = new Date(eventTime(a) || '0').getTime();
    const timeB = new Date(eventTime(b) || '0').getTime();
    return timeB - timeA;
  });

  // Priority 2: Check for DNF event
  const dnfEvent = sortedEvents.find(e => e.event_type === 'dnf');
  if (dnfEvent) {
    const status: RunnerDisplayStatus = 'dnf';
    return {
      status,
      statusLabel: 'DNF',
      statusCode: toStatusCode(status),
      isDropped: true,
      color: getStatusColor(status),
    };
  }

  // Priority 3: Check for finish event
  const finishEvent = sortedEvents.find(e => e.event_type === 'finish');
  if (finishEvent) {
    const status: RunnerDisplayStatus = 'finished';
    return {
      status,
      statusLabel: 'Finished',
      statusCode: toStatusCode(status),
      isDropped: false,
      color: getStatusColor(status),
    };
  }

  // Check for race_start event
  const hasRaceStart = sortedEvents.some(e => e.event_type === 'race_start');

  // Priority 4: Race is draft and runner has no race_start
  if (raceStatus === 'draft' && !hasRaceStart) {
    const status: RunnerDisplayStatus = 'waiting_for_race_start';
    return {
      status,
      statusLabel: 'Waiting for race start',
      statusCode: toStatusCode(status),
      isDropped: false,
      color: getStatusColor(status),
    };
  }

  // Priority 5: Race is active but runner hasn't started
  if (raceStatus === 'active' && !hasRaceStart && sortedEvents.length === 0) {
    const status: RunnerDisplayStatus = 'runner_start_pending';
    return {
      status,
      statusLabel: 'Runner start pending',
      statusCode: toStatusCode(status),
      isDropped: false,
      color: getStatusColor(status),
    };
  }

  // Priority 6: Runner has events, calculate "Enroute to" status
  const latestEvent = sortedEvents[0];
  if (sortedEvents.length > 0 && latestEvent) {
    const enrouteStatus: RunnerDisplayStatus = 'enroute';
    const enrouteColor = getStatusColor(enrouteStatus);

    // Handle race_start event - heading to first station
    if (latestEvent.event_type === 'race_start') {
      const nextStation = getNextStation(stations, null);
      const stationName = nextStation?.name || 'AS1';
      return {
        status: enrouteStatus,
        statusLabel: `Enroute to ${stationName}`,
        statusCode: toStatusCode(enrouteStatus),
        nextStation: stationName,
        nextStationId: nextStation?.id,
        isDropped: false,
        color: enrouteColor,
      };
    }

    // For a check_in event at a full check-in+check-out station, the runner
    // is AT that station (they arrived and haven't checked out yet). The device
    // selected to that station needs to see "At {station}" — not "Enroute to
    // {next}" — so the volunteer there knows the runner is still with them.
    // For check_out / pass / check_in-at-check-in-only, fall through to the
    // existing "Enroute to next station" logic below.
    if (latestEvent.event_type === 'check_in' && latestEvent.station_id) {
      const atStation = stations.find(s => s.id === latestEvent.station_id);
      if (atStation && atStation.check_mode === 'check_in_out') {
        const atStationStatus: RunnerDisplayStatus = 'at_station';
        return {
          status: atStationStatus,
          statusLabel: `At ${atStation.name}`,
          statusCode: toStatusCode(atStationStatus),
          nextStation: atStation.name,
          isDropped: false,
          color: getStatusColor(atStationStatus),
        };
      }
    }

    // For check_out / pass / check_in-at-check-in-only events - calculate next station
    if (latestEvent.station_id) {
      const nextStation = getNextStation(stations, latestEvent.station_id);

      if (nextStation) {
        return {
          status: enrouteStatus,
          statusLabel: `Enroute to ${nextStation.name}`,
          statusCode: toStatusCode(enrouteStatus),
          nextStation: nextStation.name,
          nextStationId: nextStation.id,
          isDropped: false,
          color: enrouteColor,
        };
      } else {
        // Past the last regular checkpoint: heading to the finish station.
        // getNextStation excludes check_mode='finish' from its result, so look
        // it up directly so the heading_to filter can match this segment.
        const finishStation = stations.find(s => s.check_mode === 'finish');
        return {
          status: enrouteStatus,
          statusLabel: 'Enroute to Finish',
          statusCode: toStatusCode(enrouteStatus),
          nextStation: finishStation?.name ?? 'Finish',
          nextStationId: finishStation?.id,
          isDropped: false,
          color: enrouteColor,
        };
      }
    }

    // Event has no station_id and isn't race_start (edge case)
    // Execution only reaches here when latestEvent.station_id is falsy
    const firstStation = getNextStation(stations, null);
    if (firstStation) {
      return {
        status: enrouteStatus,
        statusLabel: `Enroute to ${firstStation.name}`,
        statusCode: toStatusCode(enrouteStatus),
        nextStation: firstStation.name,
        nextStationId: firstStation.id,
        isDropped: false,
        color: enrouteColor,
      };
    }

    // No stations configured
    return {
      status: enrouteStatus,
      statusLabel: 'On Course',
      statusCode: toStatusCode(enrouteStatus),
      isDropped: false,
      color: enrouteColor,
    };
  }

  // Priority 7: Unknown fallback
  const status: RunnerDisplayStatus = 'unknown';
  return {
    status,
    statusLabel: 'Unknown',
    statusCode: toStatusCode(status),
    isDropped: false,
    color: getStatusColor(status),
  };
}
